import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BackgroundSoundListItemResponseDto } from '@shared/services/apiService/apiService';

import {
  clampPercentage,
  getSoundDefaultVolume,
  ThinkingSoundDraft,
} from '../utils/ambienceSettings.utils';

import { useStyles } from './styles';
import SoundSelectRow from './SoundSelectRow';
import SoundVolumeRow from './SoundVolumeRow';

const getSelectableThinkingSounds = (
  sounds: BackgroundSoundListItemResponseDto[],
  selectedIds: string[],
  currentSoundId?: string,
) => sounds.filter((sound) => sound.id === currentSoundId || !selectedIds.includes(sound.id));

export type ThinkingSoundCardProps = {
  sound: ThinkingSoundDraft;
  sounds: BackgroundSoundListItemResponseDto[];
  selectedThinkingIds: string[];
  disabled: boolean;
  shouldShowProbabilityErrors: boolean;
  onProbabilityBlur: () => void;
  onChange: (updater: (item: ThinkingSoundDraft) => ThinkingSoundDraft) => void;
  onRemove: () => void;
  onStopPreview: (key: string) => void;
  onTogglePreview: (key: string, fileUrl?: string, volume?: number) => void;
  onUpdatePreviewVolume: (key: string, volume: number) => void;
  isPreviewPlaying: boolean;
  previewButtonLabel: string;
  muteButtonLabel: string;
};

export const ThinkingSoundCard: React.FC<ThinkingSoundCardProps> = ({
  sound,
  sounds,
  selectedThinkingIds,
  disabled,
  shouldShowProbabilityErrors,
  onProbabilityBlur,
  onChange,
  onRemove,
  onStopPreview,
  onTogglePreview,
  onUpdatePreviewVolume,
  isPreviewPlaying,
  previewButtonLabel,
  muteButtonLabel,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const currentSound = sounds.find((item) => item.id === sound.soundId);
  const previewKey = `thinking-${sound.clientId}`;
  const availableThinkingSounds = getSelectableThinkingSounds(
    sounds,
    selectedThinkingIds.filter((selectedId) => selectedId !== sound.soundId),
    sound.soundId,
  );

  return (
    <Box className={classes.itemCard}>
      <SoundSelectRow
        label={t('campaignPage.ambienceSettings.sound')}
        disabled={disabled}
        value={sound.soundId}
        options={availableThinkingSounds}
        missingValueLabel={
          sound.missing ? t('campaignPage.ambienceSettings.missingSoundOption') : undefined
        }
        isPlaying={isPreviewPlaying}
        canPreview={Boolean(currentSound?.fileUrl)}
        previewButtonLabel={previewButtonLabel}
        onChange={(nextSoundId) => {
          onStopPreview(previewKey);
          onChange((item) => ({ ...item, soundId: nextSoundId, missing: false }));
        }}
        onTogglePreview={() => onTogglePreview(previewKey, currentSound?.fileUrl, sound.volume)}
      />
      {sound.missing ? (
        <Box>
          <Typography variant="body2" className={classes.disabledNotice}>
            {t('campaignPage.ambienceSettings.missingSoundWarning')}
          </Typography>
          <Button
            variant="text"
            size="small"
            sx={{ px: 0, minWidth: 'auto' }}
            disabled={disabled || !availableThinkingSounds.length}
            onClick={() => {
              const replacementSound = availableThinkingSounds[0];
              if (!replacementSound) {
                return;
              }
              onChange((item) => ({
                ...item,
                soundId: replacementSound.id,
                missing: false,
              }));
            }}>
            {t('campaignPage.ambienceSettings.replaceMissingSound')}
          </Button>
        </Box>
      ) : null}

      <SoundVolumeRow
        label={t('campaignPage.ambienceSettings.volume')}
        disabled={disabled}
        value={sound.volume}
        hasSound
        muteButtonLabel={muteButtonLabel}
        onChange={(nextVolume) => {
          onUpdatePreviewVolume(previewKey, nextVolume);
          onChange((item) => ({
            ...item,
            volume: nextVolume,
            previousVolume: nextVolume > 0 ? nextVolume : item.previousVolume,
          }));
        }}
        onToggleMute={() =>
          onChange((item) => {
            const selectedSound = sounds.find((candidate) => candidate.id === item.soundId);
            const nextVolume =
              item.volume > 0 ? 0 : item.previousVolume || getSoundDefaultVolume(selectedSound);

            onUpdatePreviewVolume(previewKey, nextVolume);

            return {
              ...item,
              volume: nextVolume,
              previousVolume:
                item.volume > 0
                  ? item.volume
                  : item.previousVolume || getSoundDefaultVolume(selectedSound),
            };
          })
        }
      />

      <Box className={classes.probabilityRow}>
        <Typography variant="body2">{t('campaignPage.ambienceSettings.probability')}</Typography>
        <Box className={classes.probabilityEditor}>
          <Box className={classes.probabilityFieldGroup}>
            <TextField
              type="number"
              size="small"
              className={classes.probabilityInput}
              disabled={disabled}
              value={sound.probability}
              inputProps={{ min: 0, max: 100, step: 1 }}
              onChange={(event) => {
                const nextValue = event.target.value;
                const parsedValue = nextValue === '' ? 0 : Number(nextValue);

                onChange((item) => ({
                  ...item,
                  probability: clampPercentage(parsedValue),
                }));
              }}
              onBlur={onProbabilityBlur}
              error={shouldShowProbabilityErrors}
              helperText={
                shouldShowProbabilityErrors
                  ? t('campaignPage.ambienceSettings.probabilityTotalError')
                  : undefined
              }
            />
          </Box>
          <Typography variant="body2">%</Typography>
        </Box>
        <Box />
        <Button
          variant="outlined"
          color="inherit"
          disabled={disabled}
          onClick={() => {
            onStopPreview(previewKey);
            onRemove();
          }}>
          {t('campaignPage.ambienceSettings.remove')}
        </Button>
      </Box>
    </Box>
  );
};

export default ThinkingSoundCard;
